/**
 * useEventAnalytics — shared fetch hook for the four V2.5-B template
 * pages. Hits /api/dashboard/:eventId/{stats,extras,compare,drilldown}
 * with per-slice state and 404-tolerant compare (AnalyticsPage
 * precedent).
 *
 * Why a hook and not a context: each page may want different slices
 * (post-mortem doesn't need /drilldown; daily-live needs /stats on a
 * poll). A hook lets each page request what it needs and degrade
 * cleanly when a slice returns 5xx.
 *
 * Philosophy:
 *   - Every slice has its own loading + error + data triad so UI
 *     panes can render independently.
 *   - Compare + drilldown + extras errors do NOT block stats.
 *   - AbortControllers on every fetch so event-switch cleans up.
 *   - pollStatsEveryMs optional — pass 30000 for daily-live, omit
 *     otherwise.
 *
 * V2.5-B Fork (2026-04-20).
 */

import { useEffect, useRef, useState } from 'react'

export interface HourlyPoint {
  hour: number
  orders: number
  revenue: number
  riders: number
}

export interface DashboardStats {
  source: string
  event: {
    event_id: number
    event_name: string
    event_date: string
    status: string
  }
  metrics: {
    revenue_total: number
    ride_gross: number
    guests: number
    riders_per_hour: number
    orders_per_hour: number
  }
  hourly: HourlyPoint[]
  generated_at: string
}

export interface PlanRow {
  rank: number
  plan_name: string
  orders: number
  revenue: number
  pct_of_orders: number
}

export interface KioskRow {
  rank: number
  kiosk_group_name: string
  placement_type: string | null
  orders: number
  revenue: number
  new_band_orders: number
  reload_orders: number
}

export interface TopAttractionRow {
  rank: number
  attraction_id: number
  attraction_name: string
  attraction_type: string
  scans: number
  unique_guests: number
  revenue: number
  repeat_play_pct: number
}

export interface DailyBreakdownRow {
  day: string
  scans: number
  guests: number
  revenue: number
  paid_scans: number
}

export interface DashboardExtras {
  source: string
  event_id: number
  plan_mix: PlanRow[]
  kiosk_throughput: KioskRow[]
  top_attractions: TopAttractionRow[]
  daily_breakdown?: DailyBreakdownRow[]
  geography: { status: string; reason?: string }
  generated_at: string
}

export interface YoyPctMap {
  revenue_total?: number
  ride_gross?: number
  guests?: number
  riders_per_hour?: number
  orders_per_hour?: number
}

export interface DrilldownRow {
  band_id: number
  band_barcode: string | null
  plan_name: string
  purchase_channel: string | null
  scans: number
  duration_seconds: number
  total_spent: number
  first_scan_utc: string | null
  last_scan_utc: string | null
}

export interface DrilldownEnvelope {
  source: string
  event_id: number
  page: number
  page_size: number
  total: number
  rows: DrilldownRow[]
  generated_at: string
}

export interface UseEventAnalyticsOptions {
  eventId: number | null
  /** Fetch /stats on this cadence (ms). Default: one-shot. */
  pollStatsEveryMs?: number
  /** Opt out of /extras fetch (post-mortem doesn't need it in all panes). */
  fetchExtras?: boolean
  /** Opt out of /compare fetch. */
  fetchCompare?: boolean
  /**
   * Fetch /drilldown? Only customer-segment uses it in V2.5-B. Default false.
   * Pass pageSize to widen the default 25-row page.
   */
  fetchDrilldown?: boolean | { pageSize: number }
}

export interface UseEventAnalyticsResult {
  stats: DashboardStats | null
  statsLoading: boolean
  statsError: string | null
  extras: DashboardExtras | null
  extrasLoading: boolean
  extrasAvailable: boolean
  yoy: YoyPctMap | null
  drilldown: DrilldownEnvelope | null
  drilldownLoading: boolean
  drilldownError: string | null
}

export function useEventAnalytics({
  eventId,
  pollStatsEveryMs,
  fetchExtras = true,
  fetchCompare = true,
  fetchDrilldown = false,
}: UseEventAnalyticsOptions): UseEventAnalyticsResult {
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [statsLoading, setStatsLoading] = useState<boolean>(false)
  const [statsError, setStatsError] = useState<string | null>(null)

  const [extras, setExtras] = useState<DashboardExtras | null>(null)
  const [extrasLoading, setExtrasLoading] = useState<boolean>(false)
  const [extrasAvailable, setExtrasAvailable] = useState<boolean>(true)

  const [yoy, setYoy] = useState<YoyPctMap | null>(null)

  const [drilldown, setDrilldown] = useState<DrilldownEnvelope | null>(null)
  const [drilldownLoading, setDrilldownLoading] = useState<boolean>(false)
  const [drilldownError, setDrilldownError] = useState<string | null>(null)

  const pollSeqRef = useRef(0)

  // Clear all event-scoped state on event switch.
  useEffect(() => {
    setStats(null)
    setStatsError(null)
    setExtras(null)
    setExtrasAvailable(true)
    setYoy(null)
    setDrilldown(null)
    setDrilldownError(null)
    if (eventId == null) {
      setStatsLoading(false)
      setExtrasLoading(false)
      setDrilldownLoading(false)
    }
  }, [eventId])

  // /stats — one-shot or polled, per options.
  useEffect(() => {
    if (eventId == null) return
    let cancelled = false
    let intervalId: number | undefined
    pollSeqRef.current = 0

    const fetchOnce = (initial: boolean) => {
      if (cancelled) return
      pollSeqRef.current += 1
      const mySeq = pollSeqRef.current
      const controller = new AbortController()
      if (initial) {
        setStatsLoading(true)
        setStatsError(null)
      }
      fetch(`/api/dashboard/${eventId}/stats`, {
        credentials: 'same-origin',
        signal: controller.signal,
      })
        .then((res) => {
          if (!res.ok) {
            const err = new Error(`HTTP ${res.status}`) as Error & { httpStatus?: number }
            err.httpStatus = res.status
            throw err
          }
          return res.json() as Promise<DashboardStats>
        })
        .then((data) => {
          if (cancelled || mySeq !== pollSeqRef.current) return
          setStats(data)
          setStatsError(null)
          setStatsLoading(false)
        })
        .catch((err: unknown) => {
          if (cancelled || mySeq !== pollSeqRef.current) return
          if (err && (err as { name?: string }).name === 'AbortError') return
          const httpStatus = (err as { httpStatus?: number })?.httpStatus
          if (httpStatus === 401 || httpStatus === 403 || httpStatus === 404) {
            // Access lost — clear everything so stale protected KPIs
            // don't linger.
            setStats(null)
            setExtras(null)
            setExtrasAvailable(false)
            setYoy(null)
            setDrilldown(null)
          }
          setStatsError(err instanceof Error ? err.message : 'failed')
          if (initial) setStatsLoading(false)
        })
    }

    fetchOnce(true)
    if (pollStatsEveryMs && pollStatsEveryMs > 0) {
      intervalId = window.setInterval(() => fetchOnce(false), pollStatsEveryMs)
    }

    return () => {
      cancelled = true
      if (intervalId !== undefined) window.clearInterval(intervalId)
    }
  }, [eventId, pollStatsEveryMs])

  // /extras — one-shot.
  useEffect(() => {
    if (eventId == null || !fetchExtras) return
    const controller = new AbortController()
    let cancelled = false
    setExtrasLoading(true)
    fetch(`/api/dashboard/${eventId}/extras`, {
      credentials: 'same-origin',
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json() as Promise<DashboardExtras>
      })
      .then((data) => {
        if (cancelled) return
        setExtras(data)
        setExtrasAvailable(true)
        setExtrasLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        if (err && (err as { name?: string }).name === 'AbortError') return
        setExtras(null)
        setExtrasAvailable(false)
        setExtrasLoading(false)
      })
    return () => {
      cancelled = true
      controller.abort()
    }
  }, [eventId, fetchExtras])

  // /compare — 404-tolerant.
  useEffect(() => {
    if (eventId == null || !fetchCompare) return
    const controller = new AbortController()
    let cancelled = false
    fetch(`/api/dashboard/${eventId}/compare`, {
      credentials: 'same-origin',
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json() as Promise<{ yoy_pct?: YoyPctMap }>
      })
      .then((data) => {
        if (cancelled) return
        setYoy(data && data.yoy_pct ? data.yoy_pct : null)
      })
      .catch((err) => {
        if (cancelled) return
        if (err && (err as { name?: string }).name === 'AbortError') return
        setYoy(null)
      })
    return () => {
      cancelled = true
      controller.abort()
    }
  }, [eventId, fetchCompare])

  // /drilldown — one-shot, first page only (customer-segment).
  useEffect(() => {
    if (eventId == null || !fetchDrilldown) return
    const pageSize =
      typeof fetchDrilldown === 'object' ? fetchDrilldown.pageSize : 100
    const controller = new AbortController()
    let cancelled = false
    setDrilldownLoading(true)
    setDrilldownError(null)
    fetch(
      `/api/dashboard/${eventId}/drilldown?page=1&pageSize=${pageSize}&sort=scans&direction=desc`,
      { credentials: 'same-origin', signal: controller.signal },
    )
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json() as Promise<DrilldownEnvelope>
      })
      .then((data) => {
        if (cancelled) return
        setDrilldown(data)
        setDrilldownLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        if (err && (err as { name?: string }).name === 'AbortError') return
        setDrilldownError(err instanceof Error ? err.message : 'failed')
        setDrilldownLoading(false)
      })
    return () => {
      cancelled = true
      controller.abort()
    }
  }, [eventId, fetchDrilldown])

  return {
    stats,
    statsLoading,
    statsError,
    extras,
    extrasLoading,
    extrasAvailable,
    yoy,
    drilldown,
    drilldownLoading,
    drilldownError,
  }
}
